import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useGame } from "@/context/GameContext";
import { ACHIEVEMENTS, BUSINESSES, RIVAL, WEEK_LENGTH } from "@/data/gameData";
import { Trophy, Sparkles } from "lucide-react";
import Avatar from "@/components/avatar/Avatar";
import Mascot from "@/components/avatar/Mascot";

const EndOfDay = ({ open, onClose }) => {
  const { state, endDay } = useGame();

  const handleContinue = () => {
    endDay();
    onClose();
  };

  const todayRows = Object.values(BUSINESSES)
    .map((b) => ({ b, e: state.todayEarnings[b.id] }))
    .filter(({ e }) => e && e.jobs > 0);
  const todayProfit = todayRows.reduce((s, { e }) => s + e.profit, 0);

  const earnedList = state.achievements
    .map((id) => ACHIEVEMENTS.find((a) => a.id === id))
    .filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-3xl border-4 border-violet-300 bg-white p-0 overflow-hidden" data-testid="end-of-day-modal">
        <div className="bg-gradient-to-br from-violet-300 to-fuchsia-400 p-5 border-b-4 border-violet-500 text-center">
          <div className="flex items-end justify-center gap-3 mb-2">
            <Avatar avatar={state.avatar} size={70} testid="eod-avatar" />
            <div className="text-5xl animate-bounce-slow">🌙</div>
            <Mascot pet={state.avatar.pet} size={60} cheer={open} testid="eod-mascot" />
          </div>
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-bold text-slate-900">
              End of Day {state.day}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm font-semibold text-violet-900 mt-1">
            Great work! Here&apos;s your summary.
          </p>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-3 text-center">
              <div className="text-xs font-black uppercase text-emerald-700">Jobs today</div>
              <div className="font-display text-3xl font-bold text-emerald-800">
                {state.dailyJobsCompleted}
              </div>
            </div>
            <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-3 text-center">
              <div className="text-xs font-black uppercase text-amber-700">Total earned</div>
              <div className="font-display text-3xl font-bold text-amber-800">
                ${state.totalEarned}
              </div>
            </div>
          </div>

          <div className="bg-orange-50 border-2 border-orange-200 rounded-2xl p-4" data-testid="eod-race">
            <div className="text-xs font-black uppercase tracking-wider text-orange-700 mb-1">
              🏁 Top Boss race · Week {state.week} · {state.day % WEEK_LENGTH === 0 ? "Final day!" : `${WEEK_LENGTH - (state.day % WEEK_LENGTH)} days left`}
            </div>
            <div className="flex justify-between text-sm font-heading font-black text-slate-800">
              <span>You: ${state.weekEarnings}</span>
              <span>{RIVAL.name}: ${state.rival.weekEarnings}</span>
            </div>
            {state.day % WEEK_LENGTH === 0 && (
              <div className="text-xs font-bold text-slate-600 mt-1">Results revealed when you end the day!</div>
            )}
          </div>

          {todayRows.length > 0 && (
            <div className="bg-slate-50 border-2 border-slate-200 rounded-2xl p-4" data-testid="eod-earnings-breakdown">
              <div className="text-xs font-black uppercase tracking-wider text-slate-600 mb-2">
                Today&apos;s profit by business · ${todayProfit}
              </div>
              <div className="space-y-1.5">
                {todayRows.map(({ b, e }) => (
                  <div key={b.id} className="flex items-center justify-between text-sm font-heading font-bold text-slate-800" data-testid={`eod-earn-${b.id}`}>
                    <span>{b.emoji} {b.name} <span className="text-slate-400 text-xs">· {e.jobs} {e.jobs === 1 ? "job" : "jobs"}</span></span>
                    <span className={e.profit >= 0 ? "text-emerald-700" : "text-rose-700"}>${e.profit}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-4">
            <div className="flex items-center gap-2 text-blue-700 mb-1">
              <Sparkles className="w-4 h-4" />
              <span className="text-xs font-black uppercase tracking-wider">Piggy Interest</span>
            </div>
            <p className="text-sm font-heading font-semibold text-slate-700">
              You&apos;ll earn <span className="text-blue-700 font-black">+5%</span> on your ${state.piggyBank} piggy bank overnight!
            </p>
          </div>

          {earnedList.length > 0 && (
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 text-yellow-800 mb-2">
                <Trophy className="w-4 h-4" />
                <span className="text-xs font-black uppercase tracking-wider">
                  Achievements ({earnedList.length})
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {earnedList.map((a) => (
                  <div
                    key={a.id}
                    className="bg-white border-2 border-yellow-300 rounded-xl px-2 py-1 text-xs font-heading font-bold text-slate-800 flex items-center gap-1"
                    data-testid={`achievement-${a.id}`}
                  >
                    <span>{a.icon}</span>
                    {a.title}
                  </div>
                ))}
              </div>
            </div>
          )}

          <Button
            onClick={handleContinue}
            data-testid="end-day-button"
            className="w-full bg-violet-500 hover:bg-violet-600 text-white font-black font-heading text-base py-6 rounded-2xl btn-3d border-2 border-violet-600"
          >
            Start Day {state.day + 1} →
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EndOfDay;
